import type { AdminUserRow } from '../services/api';

const DEFAULT_SSO_SYNC_DISABLED_REASON =
    'SSO users without a subject ID sync automatically the next time they sign in.';

export interface AdminUserAuth0SyncState {
    canSync: boolean;
    disabledReason: string | null;
}

function hasSubjectId(user: Pick<AdminUserRow, 'subjectId'>): boolean {
    return typeof user.subjectId === 'string' && user.subjectId.trim().length > 0;
}

export function getAdminUserAuth0SyncState(
    user: Pick<
        AdminUserRow,
        'subjectId' | 'isSso' | 'isSyncedToAuth0' | 'isSyncableToAuth0' | 'auth0SyncDisabledReason'
    >,
): AdminUserAuth0SyncState {
    if (user.isSyncedToAuth0 || hasSubjectId(user)) {
        return {
            canSync: false,
            disabledReason: null,
        };
    }

    if (user.isSyncableToAuth0 === false) {
        return {
            canSync: false,
            disabledReason: user.auth0SyncDisabledReason ?? DEFAULT_SSO_SYNC_DISABLED_REASON,
        };
    }

    if (user.isSso) {
        return {
            canSync: false,
            disabledReason: user.auth0SyncDisabledReason ?? DEFAULT_SSO_SYNC_DISABLED_REASON,
        };
    }

    return {
        canSync: true,
        disabledReason: null,
    };
}

const AUTH0_ROOT_PROFILE_UPDATABLE_PREFIXES = ['auth0|', 'email|', 'sms|'] as const;

export function canUpdateAuth0RootProfileAttributes(subjectId: string | null | undefined): boolean {
    if (typeof subjectId !== 'string' || subjectId.trim() === '') {
        return true;
    }

    return AUTH0_ROOT_PROFILE_UPDATABLE_PREFIXES.some((prefix) => subjectId.startsWith(prefix));
}
